import { Button } from '@/components/Button/Button';
import { useIngest } from '@/components/Ingest/useIngest';
import { LoadingSpinner } from '@/components/Loading';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuPortal,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu';
import { useForm } from '@/components/ui/Form';
import { Icon } from '@/components/ui/Icon';
import { ReusableTooltip } from '@/components/ui/ReusableTooltip';
import useAnalytics from '@/lib/analytics/useAnalytics';
import { sanitizeYoutubeUrl } from '@/utils/youtubeUrl';
import { isYoutubeUrl, websiteUrlSchema } from '@unriddle/validators/url';
import { LinkIcon, ScanBarcode } from 'lucide-react';
import { CircleHelp } from 'lucide-react';
import { useState } from 'react';
import { z } from 'zod';
import ButtonCard from '../ButtonCard';

const urlOrDoiSchema = z.string().refine(
  (value) => {
    const urlResult = websiteUrlSchema.safeParse(value);
    if (urlResult.success) return true;

    const doiRegex = /^10\.\d{4,}\/[^\s\/]+$/i;
    if (doiRegex.test(value)) return true;

    const pmidRegex = /^\d+$/;
    if (pmidRegex.test(value)) return true;

    try {
      const withPrefix = `https://doi.org/${value}`;
      const prefixedResult = websiteUrlSchema.safeParse(withPrefix);
      if (prefixedResult.success) {
        // Additional check to ensure it follows DOI pattern
        return /^https:\/\/doi\.org\/10\.\d{4,}\/[^\s\/]+$/i.test(withPrefix);
      }
    } catch {}

    return false;
  },
  {
    message: 'Please enter a valid website URL, DOI, or PMID identifier',
  },
);

export const UrlImportForm = ({
  setOpenMenu,
  landingPage = false,
}: {
  setOpenMenu: (open: boolean) => void;
  landingPage?: boolean;
}) => {
  const form = useForm({
    schema: z.object({
      url: urlOrDoiSchema,
    }),
    defaultValues: {
      url: '',
    },
  });

  const {
    onWebsiteUrlSubmit,
    onSubmitYTVideo,
    isWebsiteIngestLoading,
    isChatCreationLoading,
    isPdfIngestLoading,
    websiteIngestError,
    isGenerating,
    isYTIngestLoading,
  } = useIngest();
  const { track } = useAnalytics();
  const isLoading =
    isWebsiteIngestLoading ||
    isPdfIngestLoading ||
    isChatCreationLoading ||
    isGenerating ||
    isYTIngestLoading;

  const handleSubmit = async (values: { url: string }) => {
    track(landingPage ? 'landing_button_clicked' : 'plus_menu_button_clicked', {
      button: 'import_url',
    });
    let urlToProcess = values.url.trim();

    const doiRegex = /^10\.\d{4,}\/[^\s\/]+$/i;
    if (doiRegex.test(urlToProcess)) {
      urlToProcess = `https://doi.org/${urlToProcess}`;
    }

    const pmidRegex = /^\d+$/;
    if (pmidRegex.test(urlToProcess)) {
      urlToProcess = `https://pubmed.ncbi.nlm.nih.gov/${urlToProcess}/`;
    }

    if (isYoutubeUrl(urlToProcess)) {
      const sanitizedUrl = sanitizeYoutubeUrl(urlToProcess);
      await onSubmitYTVideo({ url: sanitizedUrl });
    } else {
      await onWebsiteUrlSubmit({ url: urlToProcess });
    }

    if (!isLoading) {
      setOpenMenu(false);
      form.reset();
    }
  };

  const handleCancel = () => {
    setOpenMenu(false);
    form.reset();
  };

  return (
    <form
      onSubmit={form.handleSubmit(handleSubmit)}
      className="flex flex-col p-2"
    >
      <div className="w-full flex flex-col items-start">
        <div className="flex items-center gap-1 mb-2 pl-2 pt-2">
          <label className="text-base font-semibold">URL</label>
          <ReusableTooltip content="Website, PDF or YouTube URL">
            <div>
              <Icon as={CircleHelp} className="text-textGray4" size="sm" />
            </div>
          </ReusableTooltip>
        </div>
        <input
          type="text"
          {...form.register('url')}
          className="bg-popover flex flex-row items-center justify-between px-3 h-9 rounded-[10px] border  border-border focus:outline-none w-full text-sm leading-[100%] tracking-[-0.32px]"
          placeholder="Website, PDF or YouTube URL"
          disabled={isLoading}
          autoFocus
        />
        {form.formState.errors.url && (
          <p className="text-red mt-2 text-xs">
            {form.formState.errors.url.message}
          </p>
        )}
        {websiteIngestError && (
          <p className="text-red mt-2 text-xs">
            Error: Please try another link
          </p>
        )}
      </div>

      <div className="mt-2 flex flex-row gap-2 justify-end">
        <>
          <Button
            variant="secondary"
            type="button"
            size="xs"
            onClick={handleCancel}
            className="h-8 px-2.5 text-sm"
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            variant="default"
            className="px-2.5 h-8 !text-sm"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <LoadingSpinner variant="small" color="gray" />
                <span className="text-sm">Generating</span>
              </>
            ) : (
              'Import'
            )}
          </Button>
        </>
      </div>
    </form>
  );
};

export const IdImportForm = ({
  setOpenMenu,
  landingPage = false,
}: {
  setOpenMenu: (open: boolean) => void;
  landingPage?: boolean;
}) => {
  const form = useForm({
    schema: z.object({
      sourcesId: z.string(),
    }),
    defaultValues: {
      sourcesId: '',
    },
  });

  const {
    onWebsiteUrlSubmit,
    isWebsiteIngestLoading,
    isChatCreationLoading,
    isPdfIngestLoading,
    websiteIngestError,
    isGenerating,
    isYTIngestLoading,
  } = useIngest();
  const { track } = useAnalytics();
  const isLoading =
    isWebsiteIngestLoading ||
    isPdfIngestLoading ||
    isChatCreationLoading ||
    isGenerating ||
    isYTIngestLoading;

  const handleSubmit = async (values: { sourcesId: string }) => {
    track(landingPage ? 'landing_button_clicked' : 'plus_menu_button_clicked', {
      button: 'import_id',
    });
    let idToProcess = values.sourcesId.trim();

    const doiRegex = /^10\.\d{4,}\/[^\s\/]+$/i;
    const pmidRegex = /^\d+$/;
    const arxivRegex = /^(?:arxiv:)?(\d+\.\d+|\d+\/\d+)$/i;

    if (doiRegex.test(idToProcess)) {
      idToProcess = `https://doi.org/${idToProcess}`;
    } else if (pmidRegex.test(idToProcess)) {
      idToProcess = `https://pubmed.ncbi.nlm.nih.gov/${idToProcess}/`;
    } else if (arxivRegex.test(idToProcess)) {
      const arxivMatch = idToProcess.match(arxivRegex);
      if (arxivMatch?.[1]) {
        idToProcess = `https://arxiv.org/pdf/${arxivMatch[1]}`;
      } else {
        return;
      }
    }

    await onWebsiteUrlSubmit({ url: idToProcess });

    if (!isLoading) {
      setOpenMenu(false);
      form.reset();
    }
  };

  const handleCancel = () => {
    setOpenMenu(false);
    form.reset();
  };

  return (
    <form
      onSubmit={form.handleSubmit(handleSubmit)}
      className="flex flex-col p-2"
    >
      <div className="w-full flex flex-col items-start">
        <div className="flex items-center gap-1 mb-2 pl-2 pt-2">
          <label className="text-base font-semibold">ID</label>
          <ReusableTooltip content="DOI, PMID, arXiv">
            {/* <ReusableTooltip content="DOI, PMID, arXiv and websites"> */}
            <div>
              <Icon as={CircleHelp} className="text-textGray4" size="sm" />
            </div>
          </ReusableTooltip>
        </div>
        <input
          type="text"
          {...form.register('sourcesId')}
          className="bg-popover flex flex-row items-center justify-between px-3 h-9 rounded-[10px] border  border-border focus:outline-none w-full text-sm leading-[100%] tracking-[-0.32px]"
          placeholder="DOI, PMID, arXiv"
          disabled={isLoading}
          autoFocus
        />
        {form.formState.errors.sourcesId && (
          <p className="text-red mt-2 text-xs">
            {form.formState.errors.sourcesId.message}
          </p>
        )}
        {websiteIngestError && (
          <p className="text-red mt-2 text-xs">
            Error: Please try another link
          </p>
        )}
      </div>

      <div className="mt-2 flex flex-row gap-2 justify-end">
        <>
          <Button
            variant="secondary"
            type="button"
            size="xs"
            onClick={handleCancel}
            className="h-8 px-2.5 text-sm"
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            variant="default"
            className="px-2.5 h-8 !text-sm"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <LoadingSpinner variant="small" color="gray" />
                <span className="text-sm">Generating</span>
              </>
            ) : (
              'Import'
            )}
          </Button>
        </>
      </div>
    </form>
  );
};

export const UrlImportButton = () => {
  const [openMenu, setOpenMenu] = useState(false);

  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const handleMouseEnter = (item: string) => {
    setHoveredItem(item);
  };

  const handleMouseLeave = () => {
    setHoveredItem(null);
  };

  return (
    <DropdownMenu
      open={openMenu}
      onOpenChange={(open) => {
        setOpenMenu(open);
      }}
    >
      <DropdownMenuTrigger asChild className="data-[state=open]:bg-background">
        <div>
          <ButtonCard
            className={
              openMenu ? 'bg-popover-hover/40 dark:bg-popover-hover' : ''
            }
            label="Import"
            icon={LinkIcon}
          />
        </div>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" sideOffset={8} className="w-[224px]">
        <DropdownMenuSub open={hoveredItem === 'id'}>
          <DropdownMenuSubTrigger
            onMouseEnter={() => handleMouseEnter('id')}
            onMouseLeave={handleMouseLeave}
          >
            <Icon as={ScanBarcode} />
            ID
          </DropdownMenuSubTrigger>
          <DropdownMenuPortal>
            <DropdownMenuSubContent
              sideOffset={-4}
              className="w-[272px] min-h-[122px]"
              onMouseEnter={() => handleMouseEnter('id')}
            >
              <IdImportForm setOpenMenu={setOpenMenu} landingPage />
            </DropdownMenuSubContent>
          </DropdownMenuPortal>
        </DropdownMenuSub>
        <DropdownMenuSub open={hoveredItem === 'url'}>
          <DropdownMenuSubTrigger
            onMouseEnter={() => handleMouseEnter('url')}
            onMouseLeave={handleMouseLeave}
          >
            <Icon as={LinkIcon} />
            URL
          </DropdownMenuSubTrigger>
          <DropdownMenuPortal>
            <DropdownMenuSubContent
              sideOffset={-4}
              className="w-[272px] min-h-[122px]"
              onMouseEnter={() => handleMouseEnter('url')}
            >
              <UrlImportForm setOpenMenu={setOpenMenu} landingPage />
            </DropdownMenuSubContent>
          </DropdownMenuPortal>
        </DropdownMenuSub>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
